import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileSignature, Printer } from "lucide-react";

const esc = (t) => String(t ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Read-only proof of the signed Terms & Conditions for a stay (view + print).
export default function TermsProofDialog({ sessionId, compact = false }) {
  const [open, setOpen] = useState(false);
  const [rec, setRec] = useState(null);

  const show = async () => {
    setOpen(true);
    setRec(null);
    try {
      const { data } = await api.get(`/terms/acceptance/${sessionId}`);
      setRec(data);
    } catch (e) {
      setRec({ error: formatApiErrorDetail(e.response?.data?.detail) || "Could not load the signed terms" });
    }
  };

  const print = () => {
    const d = rec.acceptedDetails || {};
    const sig = rec.acceptedSignatureType === "drawn"
      ? `<img src="${esc(rec.acceptedSignature)}" style="height:90px;border-bottom:1px solid #000"/>`
      : `<div style="font:italic 28px serif;border-bottom:1px solid #000;display:inline-block">${esc(rec.acceptedSignature)}</div>`;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Terms Accepted ${esc(rec.ticketNumber)}</title></head><body style="font-family:Arial;padding:24px">
      <h2>Signed Terms &amp; Conditions — Ticket ${esc(rec.ticketNumber)}</h2>
      <p><b>Signed by:</b> ${esc(rec.acceptedByName)} · <b>Phone:</b> ${esc(d.phone)} · <b>Plate:</b> ${esc(d.plate)} · <b>Truck/Trailer:</b> ${esc(d.truckType)} / ${esc(d.trailerType)}</p>
      <p><b>Accepted at (server time):</b> ${esc(new Date(rec.termsAcceptedAt).toLocaleString())} · <b>Version:</b> v${esc(rec.termsVersion)} (${esc(rec.termsLotScope === "all" ? "default" : "lot-specific")})</p>
      <p><b>Device:</b> ${esc(rec.acceptedIpOrDevice)}</p>
      <pre style="white-space:pre-wrap;border:1px solid #ccc;padding:12px;font-size:12px">${esc(rec.termsText)}</pre>
      <p>Signature:</p>${sig}</body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <>
      <Button data-testid={`terms-proof-open-${sessionId}`} type="button" variant="outline" onClick={show}
        className={`gap-2 border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800 ${compact ? "h-8 px-2 text-xs" : "h-10"}`}>
        <FileSignature className="h-4 w-4" /> {compact ? "T&C" : "Terms Accepted"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="terms-proof-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader><DialogTitle className="font-heading text-xl">Terms Accepted</DialogTitle></DialogHeader>
          {!rec ? <p className="text-sm text-slate-400">Loading…</p> : rec.error ? (
            <p data-testid="terms-proof-error" className="text-sm text-red-300">{rec.error}</p>
          ) : !rec.termsAccepted ? (
            <p data-testid="terms-proof-none" className="text-sm text-amber-300">No signed Terms &amp; Conditions on file for this stay (checked in before T&amp;C signing was required).</p>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-800 bg-slate-950 p-3">
                <p>Signed by: <b data-testid="terms-proof-name">{rec.acceptedByName}</b></p>
                <p>Version: <b data-testid="terms-proof-version">v{rec.termsVersion}</b> {rec.termsLotScope === "all" ? "(default)" : "(lot)"}</p>
                <p className="col-span-2">Accepted: <b data-testid="terms-proof-time">{new Date(rec.termsAcceptedAt).toLocaleString()}</b> (server time)</p>
                <p className="col-span-2 text-xs text-slate-500">Plate {rec.acceptedDetails?.plate || "—"} · Phone {rec.acceptedDetails?.phone} · Device {rec.acceptedIpOrDevice}</p>
              </div>
              <div data-testid="terms-proof-signature" className="rounded-lg bg-white p-3 text-slate-900">
                {rec.acceptedSignatureType === "drawn"
                  ? <img src={rec.acceptedSignature} alt="Driver signature" className="h-24" />
                  : <p className="font-serif text-2xl italic">{rec.acceptedSignature}</p>}
              </div>
              <pre data-testid="terms-proof-text" className="max-h-60 overflow-y-auto whitespace-pre-wrap rounded border border-slate-800 bg-slate-950 p-3 text-xs text-slate-300">{rec.termsText}</pre>
              <Button data-testid="terms-proof-print" onClick={print} className="h-10 gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]">
                <Printer className="h-4 w-4" /> Print proof
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
